import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API, useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { useSeo } from "../lib/seo";

/**
 * BusinessBilling — in-app subscription management surface.
 *
 * Businesses can:
 *   - See their current plan, status, next renewal date, and default card.
 *   - Cancel (at period end) or resume their membership.
 *   - Switch between Monthly and Yearly plans (redirects to Stripe Checkout).
 *   - Open Stripe Billing Portal to update their card / view Stripe-side invoices.
 *   - View + download the last 20 invoices without leaving the app.
 *
 * All operations are backend-mediated. No Stripe.js on the frontend.
 */

const currency = (amt, cur = "usd") => {
  if (amt == null) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (cur || "usd").toUpperCase(),
    }).format(Number(amt));
  } catch {
    return `$${Number(amt).toFixed(2)}`;
  }
};

const dateOnly = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};

const StatusPill = ({ status, willCancel, endsAt }) => {
  let label = status || "—";
  let bg = "var(--gf-surface-2)";
  let color = "var(--gf-text-muted)";
  if (willCancel) {
    label = `Cancels on ${dateOnly(endsAt)}`;
    bg = "rgba(217,119,6,0.12)";
    color = "#9a6500";
  } else if (status === "active") {
    label = "Active";
    color = "var(--gf-accent)";
  } else if (status === "trialing") {
    label = "Free trial";
    color = "var(--gf-accent)";
  } else if (status === "past_due" || status === "unpaid") {
    label = "Payment failed";
    bg = "rgba(220,38,38,0.12)";
    color = "#b91c1c";
  } else if (status === "canceled" || status === "incomplete_expired") {
    label = "Canceled";
  }
  return (
    <span
      className="inline-block text-[10px] uppercase tracking-[0.18em] rounded-full px-2 py-1"
      style={{ background: bg, color }}
      data-testid="billing-status-pill"
    >
      {label}
    </span>
  );
};

export default function BusinessBilling() {
  useSeo({ title: "Billing — BookFreely", path: "/business/billing", noindex: true });
  const { user, authHeader, refreshUser, loading } = useAuth();
  const nav = useNavigate();

  const [details, setDetails] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [switching, setSwitching] = useState(null);
  const [planPreview, setPlanPreview] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [resumeBusy, setResumeBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [d, i] = await Promise.all([
        axios.get(`${API}/subscription/details`, { headers: authHeader() }),
        axios.get(`${API}/subscription/invoices`, { headers: authHeader() }).catch((e) => ({ data: { invoices: [], error: e?.response?.data?.detail } })),
      ]);
      setDetails(d.data);
      setInvoices(i.data?.invoices || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load billing details");
    } finally {
      setInvoicesLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/business/billing"); return; }
    if (user.role !== "business") { nav("/"); return; }
    fetchAll();
  }, [loading, user?.id, fetchAll, nav]);

  // Iteration 56 (Feb 28 2026) — Auto-reconcile on return from the Stripe portal.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("from") !== "portal") return;
    (async () => {
      try { await axios.post(`${API}/subscription/reconcile`, { force: true }, { headers: authHeader() }); } catch (_) { /* best-effort */ }
      try { if (refreshUser) await refreshUser(); } catch (_) { /* noop */ }
      try { await fetchAll(); } catch (_) { /* noop */ }
      const url = new URL(window.location.href);
      url.searchParams.delete("from");
      window.history.replaceState({}, "", url.toString());
      toast.success("Subscription updated");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancelMembership = async () => {
    setCancelBusy(true);
    try {
      const r = await axios.post(`${API}/business/subscription/cancel`, {}, { headers: authHeader() });
      toast.success(r.data?.message || "Cancellation scheduled");
      setConfirmCancel(false);
      await refreshUser();
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    } finally {
      setCancelBusy(false);
    }
  };

  const resumeMembership = async () => {
    setResumeBusy(true);
    try {
      const r = await axios.post(`${API}/business/subscription/resume`, {}, { headers: authHeader() });
      toast.success(r.data?.message || "Membership resumed");
      await refreshUser();
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resume");
    } finally {
      setResumeBusy(false);
    }
  };

  const openBillingPortal = async () => {
    setPortalBusy(true);
    try {
      const r = await axios.post(`${API}/subscription/portal`, {}, { headers: authHeader() });
      const url = r?.data?.url;
      if (url) {
        window.location.assign(url);
      } else {
        toast.error("Couldn't open billing portal");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open billing portal");
    } finally {
      setPortalBusy(false);
    }
  };

  const switchPlan = async (tier) => {
    setSwitching(tier);
    try {
      const r = await axios.post(
        `${API}/subscription/checkout`,
        { tier, origin_url: window.location.origin },
        { headers: authHeader() }
      );
      const d = r?.data || {};
      const action = d.action;
      // A plan change never applies silently: show Stripe's authoritative
      // proration preview and require explicit confirmation.
      if (action === "plan_change_preview") {
        setPlanPreview({ ...d.preview, token: d.preview_token, targetTier: tier });
        setSwitching(null);
        return;
      }
      // Plan switched in place on the existing subscription — no new checkout.
      if (action === "plan_changed") {
        toast.success(d.message || "Your plan was updated.");
        try { if (refreshUser) await refreshUser(); } catch (_) { /* noop */ }
        await fetchAll();
        setSwitching(null);
        return;
      }
      // Payment due / paused → send to billing management instead of a new sub.
      if (d.blocked && action === "manage_billing") {
        toast.info(d.message || "Opening billing management…");
        if (d.url) window.location.assign(d.url);
        setSwitching(null);
        return;
      }
      if (action === "resume" && d.url) {
        toast.info(d.message || "Resuming your checkout…");
        window.location.href = d.url;
        setSwitching(null);
        return;
      }
      const url = d.url;
      if (!url || !/^https:\/\/([a-z0-9-]+\.)?stripe\.com\//i.test(url)) {
        toast.error("Checkout couldn't start — please try again in a moment.");
        setSwitching(null);
        return;
      }
      window.location.href = url;
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 503 && det?.error === "subscription_verification_unavailable") {
        toast.error(det.message || "We couldn't verify your membership just now. Please try again in a moment.");
      } else {
        toast.error((typeof det === "string" ? det : det?.message) || "Could not start checkout");
      }
      setSwitching(null);
    }
  };

  // Apply a previewed plan change after the business confirms the exact charges.
  const confirmPlanChange = async () => {
    if (!planPreview?.token) return;
    setConfirmBusy(true);
    try {
      const r = await axios.post(
        `${API}/subscription/plan-change/confirm`,
        { preview_token: planPreview.token },
        { headers: authHeader() }
      );
      toast.success(r?.data?.message || "Your plan was updated.");
      setPlanPreview(null);
      try { if (refreshUser) await refreshUser(); } catch (_) { /* noop */ }
      await fetchAll();
    } catch (e) {
      const det = e?.response?.data?.detail;
      // The charges changed since the preview → show the new numbers to re-confirm.
      if (e?.response?.status === 409 && det?.error === "proration_changed" && det?.preview) {
        setPlanPreview({ ...det.preview, token: det.preview_token, targetTier: det.preview.target_tier });
        toast.warning(det.message || "The charges changed — please review and confirm again.");
      } else if (e?.response?.status === 503 && det?.error === "subscription_verification_unavailable") {
        toast.error(det.message || "We couldn't verify your membership just now. Please try again in a moment.");
      } else {
        toast.error((typeof det === "string" ? det : det?.message) || "Could not apply the plan change.");
        setPlanPreview(null);
      }
    } finally {
      setConfirmBusy(false);
    }
  };

  const d = details || {};
  const plan = d.plan || {};
  const nextCharge = d.next_charge || {};
  const pm = d.payment_method;
  const status = d.subscription_status;
  const willCancel = !!d.cancel_at_period_end;
  const trialEnd = d.trial_end_at;
  const inTrial = status === "trialing" && trialEnd;
  const active = !!d.subscription_active;
  const tier = d.subscription_tier;
  // When the user is currently in their free trial, "access ends" means the
  // trial-end date (7 days) — NOT the eventual monthly renewal date. This is
  // what customers actually care about when cancelling: how long they get to
  // keep using the platform.
  const accessEndsAt = inTrial ? trialEnd : d.subscription_expires_at;

  const nextChargeLabel = inTrial
    ? `First charge on ${dateOnly(trialEnd)}`
    : willCancel
    ? `No further charges. Access ends ${dateOnly(accessEndsAt)}`
    : nextCharge.date
    ? `Next charge on ${dateOnly(nextCharge.date)}`
    : "—";

  return (
    <div className="App">
      <Header hideSearch />
      <div className="max-w-5xl mx-auto px-6 py-12" data-testid="billing-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Billing</div>
            <h1 className="font-display text-4xl mt-2" style={{ color: "var(--gf-text)" }}>Manage your subscription</h1>
          </div>
          <Link
            to="/business"
            data-testid="billing-back-link"
            className="text-sm rounded-full border px-4 py-2"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
          >
            ← Back to dashboard
          </Link>
        </div>

        {error && (
          <div data-testid="billing-error" className="mt-6 rounded-md border p-3 text-sm"
               style={{ borderColor: "#dc2626", background: "rgba(220,38,38,0.06)", color: "#b91c1c" }}>
            {error}
          </div>
        )}

        {/* Current plan card */}
        <div
          className="mt-8 rounded-2xl border p-6"
          style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}
          data-testid="billing-plan-card"
        >
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Current plan</div>
              <div className="font-display text-2xl mt-1" style={{ color: "var(--gf-text)" }} data-testid="billing-plan-name">
                {active ? (d.plan_label || "Business plan") : "No active membership"}
              </div>
              <div className="mt-2">
                <StatusPill status={status} willCancel={willCancel} endsAt={accessEndsAt} />
              </div>
              <div className="mt-3 text-sm" style={{ color: "var(--gf-text-soft)" }} data-testid="billing-plan-price">
                {plan.amount != null && plan.interval
                  ? `${currency(plan.amount, plan.currency)} / ${plan.interval}`
                  : "—"}
              </div>
              <div className="mt-1 text-sm" style={{ color: "var(--gf-text-muted)" }} data-testid="billing-next-charge">
                {nextChargeLabel}
              </div>
              {inTrial && (
                <div className="mt-2 text-sm" style={{ color: "var(--gf-text-muted)" }} data-testid="billing-trial-note">
                  Free trial ends {dateOnly(trialEnd)}. Cancel before then to avoid being charged.
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              {/* Iteration 56 (Feb 28 2026) — Stripe Customer Portal is now
                  the single source of truth for cancel/resume/plan change. */}
              {!active && (
                <Link
                  to="/business/subscribe"
                  data-testid="billing-resubscribe-link"
                  className="text-sm rounded-full font-medium px-4 py-2"
                  style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
                >
                  Choose a plan
                </Link>
              )}
              {user?.stripe_customer_id && (
                <button
                  data-testid="billing-manage-subscription-btn"
                  onClick={openBillingPortal}
                  disabled={portalBusy}
                  className="text-sm rounded-full font-medium px-4 py-2 disabled:opacity-50"
                  style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
                >
                  {portalBusy ? "Opening…" : "Manage subscription"}
                </button>
              )}
            </div>
          </div>

          {willCancel && (
            <div
              data-testid="billing-cancel-notice"
              className="mt-4 rounded-md border p-3 text-sm"
              style={{ borderColor: "rgba(217,119,6,0.5)", background: "rgba(217,119,6,0.06)", color: "var(--gf-text-soft)" }}
            >
              Your membership is scheduled to end on <b style={{ color: "var(--gf-text)" }}>{dateOnly(accessEndsAt)}</b>.
              You keep full access until then and won&rsquo;t be charged again. After that, your services stop appearing in search until you re-subscribe.
            </div>
          )}
        </div>

        {/* Payment method card */}
        <div
          className="mt-6 rounded-2xl border p-6"
          style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}
          data-testid="billing-pm-card"
        >
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Payment method</div>
              <div className="font-display text-lg mt-1" style={{ color: "var(--gf-text)" }} data-testid="billing-pm-summary">
                {pm ? (
                  <>
                    {String(pm.brand || "Card").toUpperCase()} •••• {pm.last4 || "—"}
                    <span className="ml-2 text-sm" style={{ color: "var(--gf-text-muted)" }}>
                      exp {String(pm.exp_month || "").padStart(2, "0")}/{String(pm.exp_year || "").slice(-2)}
                    </span>
                  </>
                ) : (
                  <span className="text-sm" style={{ color: "var(--gf-text-muted)" }}>
                    {active ? "Card on file managed by Stripe" : "No card on file"}
                  </span>
                )}
              </div>
              <div className="mt-1 text-sm" style={{ color: "var(--gf-text-muted)" }}>
                Card updates happen securely on Stripe&rsquo;s hosted portal. BookFreely never sees your card number.
              </div>
            </div>
            <button
              data-testid="billing-open-portal-btn"
              onClick={openBillingPortal}
              disabled={portalBusy || !d.stripe_customer_id}
              className="text-sm rounded-full border px-4 py-2 disabled:opacity-50"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            >
              {portalBusy ? "Opening…" : "Update payment method"}
            </button>
          </div>
        </div>

        {/* Switch plans */}
        <div
          className="mt-6 rounded-2xl border p-6"
          style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}
          data-testid="billing-switch-card"
        >
          <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Switch plan</div>
          <div className="font-display text-lg mt-1" style={{ color: "var(--gf-text)" }}>Change how you&rsquo;re billed</div>
          <div className="mt-1 text-sm" style={{ color: "var(--gf-text-muted)" }}>
            We&rsquo;ll show the exact charges and credit before anything changes — nothing is billed until you confirm.
          </div>
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <button
              data-testid="billing-switch-monthly-btn"
              onClick={() => switchPlan("monthly")}
              disabled={switching === "monthly" || tier === "monthly"}
              className="text-left rounded-xl border p-4 disabled:opacity-50"
              style={{ borderColor: tier === "monthly" ? "var(--gf-accent)" : "var(--gf-border-strong)", background: "var(--gf-surface-2)" }}
            >
              <div className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>Monthly</div>
              <div className="mt-1 font-display text-xl" style={{ color: "var(--gf-text)" }}>$19.99 <span className="text-sm" style={{ color: "var(--gf-text-muted)" }}>/mo</span></div>
              <div className="mt-2 text-sm" style={{ color: "var(--gf-text-muted)" }}>
                {tier === "monthly" ? "Your current plan" : switching === "monthly" ? "Redirecting to Stripe…" : "Switch to monthly billing"}
              </div>
            </button>
            <button
              data-testid="billing-switch-yearly-btn"
              onClick={() => switchPlan("yearly")}
              disabled={switching === "yearly" || tier === "yearly" || tier === "yearly_first"}
              className="text-left rounded-xl border p-4 disabled:opacity-50"
              style={{
                borderColor: (tier === "yearly" || tier === "yearly_first") ? "var(--gf-accent)" : "var(--gf-border-strong)",
                background: "var(--gf-surface-2)",
              }}
            >
              <div className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>Yearly</div>
              <div className="mt-1 font-display text-xl" style={{ color: "var(--gf-text)" }}>
                $199.99 <span className="text-sm" style={{ color: "var(--gf-text-muted)" }}>/yr</span>
              </div>
              <div className="mt-2 text-sm" style={{ color: "var(--gf-text-muted)" }}>
                {(tier === "yearly" || tier === "yearly_first")
                  ? "Your current plan"
                  : switching === "yearly"
                    ? "Redirecting to Stripe…"
                    : "Billed annually — no free trial"}
              </div>
            </button>
          </div>
        </div>

        {/* Invoices */}
        <div
          className="mt-6 rounded-2xl border p-6"
          style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}
          data-testid="billing-invoices-card"
        >
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Invoices</div>
              <div className="font-display text-lg mt-1" style={{ color: "var(--gf-text)" }}>Billing history</div>
            </div>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm" data-testid="billing-invoices-table">
              <thead>
                <tr style={{ color: "var(--gf-text-muted)" }}>
                  <th className="text-left py-2 pr-3 font-normal">Date</th>
                  <th className="text-left py-2 pr-3 font-normal">Invoice</th>
                  <th className="text-left py-2 pr-3 font-normal">Amount</th>
                  <th className="text-left py-2 pr-3 font-normal">Status</th>
                  <th className="text-right py-2 pl-3 font-normal">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoicesLoading && (
                  <tr><td colSpan={5} className="py-6 text-center" style={{ color: "var(--gf-text-muted)" }} data-testid="billing-invoices-loading">Loading…</td></tr>
                )}
                {!invoicesLoading && invoices.length === 0 && (
                  <tr><td colSpan={5} className="py-6 text-center" style={{ color: "var(--gf-text-muted)" }} data-testid="billing-invoices-empty">
                    No invoices yet. They&rsquo;ll appear here after your first charge.
                  </td></tr>
                )}
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-t" style={{ borderColor: "var(--gf-border)" }} data-testid={`billing-invoice-row-${inv.id}`}>
                    <td className="py-3 pr-3" style={{ color: "var(--gf-text)" }}>{dateOnly(inv.created)}</td>
                    <td className="py-3 pr-3" style={{ color: "var(--gf-text-soft)" }}>{inv.number || inv.id?.slice(-8)}</td>
                    <td className="py-3 pr-3" style={{ color: "var(--gf-text)" }}>{currency(inv.amount_paid || inv.amount_due, inv.currency)}</td>
                    <td className="py-3 pr-3">
                      <span className="text-[10px] uppercase tracking-[0.18em] rounded-full px-2 py-0.5"
                            style={{
                              background: inv.status === "paid" ? "rgba(16,185,129,0.12)" : "var(--gf-surface-2)",
                              color: inv.status === "paid" ? "#059669" : inv.status === "open" ? "#d97706" : "var(--gf-text-muted)",
                            }}>
                        {inv.status || "—"}
                      </span>
                    </td>
                    <td className="py-3 pl-3 text-right">
                      <div className="inline-flex gap-2">
                        {inv.hosted_invoice_url && (
                          <a
                            href={inv.hosted_invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`billing-invoice-view-${inv.id}`}
                            className="text-sm underline"
                            style={{ color: "var(--gf-accent)" }}
                          >
                            View
                          </a>
                        )}
                        {inv.invoice_pdf && (
                          <a
                            href={inv.invoice_pdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`billing-invoice-pdf-${inv.id}`}
                            className="text-sm underline"
                            style={{ color: "var(--gf-text-muted)" }}
                          >
                            PDF
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Plan-change proration preview + confirmation */}
        {planPreview && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.5)" }}
            data-testid="plan-change-preview-modal"
          >
            <div
              className="w-full max-w-md rounded-2xl border p-6"
              style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}
            >
              <div className="font-display text-xl" style={{ color: "var(--gf-text)" }} data-testid="plan-change-preview-title">
                Review your plan change
              </div>
              <div className="mt-1 text-sm" style={{ color: "var(--gf-text-muted)" }}>
                Switching to the <span style={{ color: "var(--gf-text)" }}>{planPreview.target_tier === "annual" ? "Yearly" : "Monthly"}</span> plan.
              </div>

              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span style={{ color: "var(--gf-text-muted)" }}>Due today</span>
                  <span style={{ color: "var(--gf-text)" }} data-testid="plan-change-amount-due">
                    {currency(planPreview.amount_due_now, planPreview.currency)}
                  </span>
                </div>
                {planPreview.credit_applied > 0 && (
                  <div className="flex justify-between">
                    <span style={{ color: "var(--gf-text-muted)" }}>Credit applied</span>
                    <span style={{ color: "#059669" }} data-testid="plan-change-credit">
                      −{currency(planPreview.credit_applied, planPreview.currency)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span style={{ color: "var(--gf-text-muted)" }}>Then renews at</span>
                  <span style={{ color: "var(--gf-text)" }} data-testid="plan-change-renewal">
                    {currency(planPreview.next_renewal, planPreview.currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: "var(--gf-text-muted)" }}>Next billing date</span>
                  <span style={{ color: "var(--gf-text)" }} data-testid="plan-change-billing-date">
                    {dateOnly(planPreview.next_billing_date)}
                  </span>
                </div>
                {planPreview.trial_ends_immediately && (
                  <div
                    className="mt-2 rounded-lg px-3 py-2 text-xs"
                    style={{ background: "rgba(217,119,6,0.12)", color: "#9a6500" }}
                    data-testid="plan-change-trial-warning"
                  >
                    Your free trial ends immediately when you switch to this plan.
                  </div>
                )}
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  data-testid="plan-change-cancel-btn"
                  onClick={() => setPlanPreview(null)}
                  disabled={confirmBusy}
                  className="flex-1 rounded-xl border px-4 py-2.5 text-sm disabled:opacity-50"
                  style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
                >
                  Keep current plan
                </button>
                <button
                  data-testid="plan-change-confirm-btn"
                  onClick={confirmPlanChange}
                  disabled={confirmBusy}
                  className="flex-1 rounded-xl px-4 py-2.5 text-sm font-medium disabled:opacity-60"
                  style={{ background: "var(--gf-accent)", color: "#fff" }}
                >
                  {confirmBusy ? "Applying…" : "Confirm & switch"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cancel confirmation modal */}
        {confirmCancel && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.5)" }}
            data-testid="billing-cancel-modal-DEPRECATED"
            hidden
          />
        )}
      </div>
      <Footer />
    </div>
  );
}
